import { useNavigate, useParams } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { releaseSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { ContentSection } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useStore } from '@/shared/store/store-context';

import { emptySectionForm, fromSection, toSectionCreate, toSectionUpdate } from '../api/mappers';
import { ContentStatusBadge } from '../components/content-badges';
import { DeleteSectionDialog } from '../components/delete-section-dialog';
import { SectionBlocksSection } from '../components/section-blocks-section';
import { SectionForm } from '../components/section-form';
import { SectionPreviewSheet } from '../components/section-preview-sheet';
import { useCreateSection, useUpdateSection } from '../hooks/use-section-mutations';
import { useSection } from '../hooks/use-sections';

const EDIT_ROUTE = '/_authed/_active/s/$storeId/content/sections/$sectionId' as const;

function SectionCreate() {
  const { t } = useTranslation('content');
  const store = useStore();
  const navigate = useNavigate();
  const create = useCreateSection(store.id);

  return (
    <PageContent>
      <PageHeader
        title={t('section.createTitle')}
        backTo={{ to: '/s/$storeId/content/sections', params: { storeId: store.id } }}
        backLabel={t('sectionsList.title')}
      />
      <SectionForm
        mode="create"
        defaultValues={emptySectionForm()}
        isSubmitting={create.isPending}
        onSubmit={async (values) => {
          const created = await create.mutateAsync(toSectionCreate(values));
          await navigate({
            to: '/s/$storeId/content/sections/$sectionId',
            params: { storeId: store.id, sectionId: created.id },
            replace: true,
          });
        }}
      >
        <FormSection title={t('sections.blocks')}>
          <p className="text-muted-foreground text-sm">{t('section.blocksAfterSave')}</p>
        </FormSection>
      </SectionForm>
    </PageContent>
  );
}

function DangerZone({ storeId, row }: { storeId: string; row: ContentSection }) {
  const { t } = useTranslation('content');
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState<ContentSection | null>(null);
  return (
    <FormSection title={t('dangerSection.title')} className="border-destructive/30">
      <p className="text-muted-foreground text-sm">{t('dangerSection.description')}</p>
      <Button
        type="button"
        variant="outline"
        className="text-destructive hover:text-destructive w-full"
        onClick={() => setDeleting(row)}
      >
        <Trash2 aria-hidden />
        {t('actions.delete')}
      </Button>
      <DeleteSectionDialog
        storeId={storeId}
        row={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        onDeleted={() => {
          // The record is gone; a dirty form must not keep us on its page.
          releaseSaveBar();
          void navigate({ to: '/s/$storeId/content/sections', params: { storeId } });
        }}
      />
    </FormSection>
  );
}

function SectionEdit() {
  const { t } = useTranslation('content');
  const format = useFormat();
  const store = useStore();
  const { can } = usePermissions();
  const { sectionId } = useParams({ from: EDIT_ROUTE });
  // Prefetched by the route loader, so this resolves from cache.
  const { data: row } = useSection(store.id, sectionId);
  const update = useUpdateSection(store.id);
  if (!row) return null;

  return (
    <PageContent>
      <PageHeader
        title={row.name}
        meta={<ContentStatusBadge status={row.status} />}
        description={
          <span title={format.utc(row.updatedAt)}>
            {t('updatedAt', { when: format.relative(row.updatedAt) })}
            {' · '}
            <code>{row.handle}</code>
            {' · '}
            {t('columns.blockCount', { count: row.blocks.length })}
          </span>
        }
        backTo={{ to: '/s/$storeId/content/sections', params: { storeId: store.id } }}
        backLabel={t('sectionsList.title')}
        actions={<SectionPreviewSheet storeId={store.id} sectionId={row.id} handle={row.handle} />}
      />
      <SectionForm
        key={row.updatedAt}
        mode="edit"
        defaultValues={fromSection(row)}
        isSubmitting={update.isPending}
        onSubmit={(values) => update.mutateAsync({ id: row.id, body: toSectionUpdate(values) })}
        dangerZone={
          <Can permission="content.write">
            <DangerZone storeId={store.id} row={row} />
          </Can>
        }
      >
        <SectionBlocksSection
          storeId={store.id}
          sectionId={row.id}
          items={row.blocks}
          canWrite={can('content.write')}
        />
      </SectionForm>
    </PageContent>
  );
}

export function SectionCreatePage() {
  return (
    <FeatureErrorBoundary name="content.sections.create">
      <SectionCreate />
    </FeatureErrorBoundary>
  );
}

export function SectionEditPage() {
  return (
    <FeatureErrorBoundary name="content.sections.edit">
      <SectionEdit />
    </FeatureErrorBoundary>
  );
}
